import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LOGO_URL, inr, CATEGORIES } from "@/data/content";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { ProofViewer } from "@/components/ProofViewer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  LogOut,
  Users,
  DollarSign,
  Wallet,
  BadgePercent,
  Search,
  Download,
  Trash2,
  Loader2,
  Home,
  CheckCircle2,
  Clock3,
  Receipt,
  BellRing,
  MessageCircle,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid, accent }) => (
  <div className="card-lux rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5 text-black" strokeWidth={2} />
      </div>
    </div>
    <div className="font-mono-lux text-2xl text-amber-50 font-700 mt-4">{value}</div>
    <div className="text-xs text-zinc-400 mt-1 tracking-wide">{label}</div>
  </div>
);

const statusStyle = {
  paid: "bg-green-500/15 text-green-400 border-green-500/30",
  partial: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  pending: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [proofRow, setProofRow] = useState(null);
  const [remindingId, setRemindingId] = useState(null);

  const remind = async (r) => {
    setRemindingId(r.id);
    try {
      const { data } = await api.post(`/registrations/${r.id}/remind`);
      const parts = [`Email ${data.email}`];
      parts.push(data.whatsapp === "not_configured" ? "WhatsApp: opened chat link" : `WhatsApp ${data.whatsapp}`);
      toast.success(`Reminder sent · ${parts.join(" · ")}`);
      if (data.whatsapp === "not_configured") window.open(data.whatsapp_link, "_blank", "noopener");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reminder failed");
    } finally {
      setRemindingId(null);
    }
  };

  const load = async () => {
    try {
      const [r, s] = await Promise.all([api.get("/registrations"), api.get("/stats")]);
      setRows(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const matchQ =
        !q ||
        r.full_name.toLowerCase().includes(q.toLowerCase()) ||
        r.email.toLowerCase().includes(q.toLowerCase()) ||
        r.phone.includes(q);
      const matchCat = catFilter === "all" || r.category === catFilter;
      const matchStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "proof"
          ? !!r.proof_submitted_at && r.payment_status !== "paid"
          : r.payment_status === statusFilter;
      return matchQ && matchCat && matchStatus;
    });
  }, [rows, q, catFilter, statusFilter]);

  const updateRow = async (id, patch) => {
    try {
      const { data } = await api.patch(`/registrations/${id}`, patch);
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      const s = await api.get("/stats");
      setStats(s.data);
      toast.success("Updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    try {
      await api.delete(`/registrations/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      const s = await api.get("/stats");
      setStats(s.data);
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    const headers = [
      "Name", "Email", "Phone", "City", "Organization", "Category", "JFC",
      "Enroller", "Members", "StayTour", "StayTourPersons", "AV",
      "Total", "Paid", "Status", "TransactionID", "ProofUploaded", "Reminders", "LastReminder", "ConfirmationEmail", "Commission", "Created",
    ];
    const lines = filtered.map((r) =>
      [
        r.full_name, r.email, r.phone, r.city, r.organization, r.category,
        r.is_jfc ? "Yes" : "No", r.enroller_name, r.accompanying_members,
        r.stay_tour ? "Yes" : "No", r.stay_tour_persons, r.av_required ? "Yes" : "No",
        r.total_amount, r.amount_paid, r.payment_status, r.transaction_id, r.proof_path ? "Yes" : "No", r.reminder_count, (r.reminder_sent_at || "").slice(0, 10), r.confirmation_email_status, r.commission,
        (r.created_at || "").slice(0, 10),
      ]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sia-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Top bar */}
      <div className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="w-9 h-9" icon="w-5 h-5" />
            <div>
              <div className="font-display gold-text font-700 text-sm">EVENT CONSOLE</div>
              <div className="text-[10px] text-zinc-500 font-mono-lux tracking-widest">
                SOUTH ICON AWARDS 2026
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-zinc-400">{user?.email}</span>
            <button
              onClick={() => navigate("/")}
              className="p-2 rounded-lg gold-border text-amber-300 hover:bg-amber-500/10"
              data-testid="dashboard-home-button"
              title="View site"
            >
              <Home className="w-4 h-4" />
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg gold-border text-amber-300 hover:bg-amber-500/10 text-sm"
              data-testid="dashboard-logout-button"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-display text-2xl sm:text-3xl text-amber-50 font-700 mb-6">
          Event Dashboard
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Users} label="Total Registrations" value={stats?.total_registrations ?? 0} accent="gold-gradient-bg" testid="admin-stat-total-registrations" />
          <StatCard icon={DollarSign} label="Total Revenue" value={inr(stats?.total_revenue)} accent="bg-amber-400" testid="admin-stat-revenue" />
          <StatCard icon={Wallet} label="Collected" value={inr(stats?.collected)} accent="bg-green-400" testid="admin-stat-collected" />
          <StatCard icon={BadgePercent} label="Team Commission" value={inr(stats?.total_commission)} accent="bg-amber-300" testid="admin-stat-commission" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Clock3} label="Payments Pending" value={stats?.pending ?? 0} accent="bg-zinc-400" testid="admin-stat-pending" />
          <StatCard icon={CheckCircle2} label="Payments Paid" value={stats?.paid ?? 0} accent="bg-green-400" testid="admin-stat-paid" />
          <StatCard icon={Receipt} label="Proofs To Verify" value={stats?.proofs_to_verify ?? 0} accent="bg-amber-400" testid="admin-stat-proofs" />
          <StatCard icon={BellRing} label="Reminders Sent" value={`${stats?.reminders_sent ?? 0} · ${stats?.reminders_due ?? 0} due`} accent="bg-amber-300" testid="admin-stat-reminders" />
          <StatCard icon={Wallet} label="Outstanding" value={inr(stats?.outstanding)} accent="bg-red-400" testid="admin-stat-outstanding" />
        </div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email or phone..."
              className="pl-9 bg-[#18181F] border-amber-500/20"
              data-testid="dashboard-search-input"
            />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-full lg:w-64 bg-[#18181F] border-amber-500/20" data-testid="dashboard-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181F] border-amber-500/25 text-zinc-100">
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.title}>{c.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full lg:w-40 bg-[#18181F] border-amber-500/20" data-testid="dashboard-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181F] border-amber-500/25 text-zinc-100">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="proof">Proof Submitted</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={exportCsv}
            className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg gold-gradient-bg text-black font-600 text-sm hover:brightness-110"
            data-testid="admin-export-csv-button"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="card-lux rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-registrations-table">
              <thead>
                <tr className="border-b border-amber-500/20 text-left text-zinc-400 font-mono-lux text-[11px] uppercase tracking-wider">
                  <th className="px-4 py-4">Awardee</th>
                  <th className="px-4 py-4">Category</th>
                  <th className="px-4 py-4">Members</th>
                  <th className="px-4 py-4">Stay&Tour</th>
                  <th className="px-4 py-4">Total</th>
                  <th className="px-4 py-4">Paid</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Proof</th>
                  <th className="px-4 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-zinc-500" data-testid="dashboard-empty">
                      No registrations found.
                    </td>
                  </tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-amber-500/10 hover:bg-amber-500/5" data-testid={`registration-row-${r.id}`}>
                    <td className="px-4 py-4">
                      <div className="text-amber-50 font-500">{r.full_name}</div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1">
                        {r.email}
                        {r.confirmation_email_status === "sent" && <CheckCircle2 className="w-3 h-3 text-green-400" title="Confirmation email sent" data-testid={`confirm-sent-${r.id}`} />}
                        {r.confirmation_email_status === "failed" && <span className="text-red-400" title="Confirmation email failed" data-testid={`confirm-failed-${r.id}`}>!</span>}
                      </div>
                      <div className="text-xs text-zinc-500">{r.phone}</div>
                      {r.is_jfc && (
                        <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                          JFC
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-zinc-300 max-w-[180px]">{r.category}</td>
                    <td className="px-4 py-4 text-zinc-300 font-mono-lux">{r.accompanying_members}</td>
                    <td className="px-4 py-4 text-zinc-300">
                      {r.stay_tour ? `Yes (${r.stay_tour_persons})` : "—"}
                    </td>
                    <td className="px-4 py-4 font-mono-lux text-amber-100">{inr(r.total_amount)}</td>
                    <td className="px-4 py-4">
                      <Input
                        type="number"
                        defaultValue={r.amount_paid}
                        onBlur={(e) => {
                          const v = parseInt(e.target.value || "0", 10);
                          if (v !== r.amount_paid) updateRow(r.id, { amount_paid: v });
                        }}
                        className="w-24 h-8 bg-[#09090B] border-amber-500/20 font-mono-lux text-xs"
                        data-testid={`paid-input-${r.id}`}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <Select
                        value={r.payment_status}
                        onValueChange={(v) => updateRow(r.id, { payment_status: v })}
                      >
                        <SelectTrigger className={`w-28 h-8 text-xs border ${statusStyle[r.payment_status] || statusStyle.pending}`} data-testid={`status-select-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181F] border-amber-500/25 text-zinc-100">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-4">
                      {r.proof_submitted_at ? (
                        <button
                          onClick={() => setProofRow(r)}
                          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg gold-border text-amber-300 hover:bg-amber-500/10"
                          data-testid={`proof-button-${r.id}`}
                          title={r.transaction_id || "View proof"}
                        >
                          <Receipt className="w-3.5 h-3.5" />
                          <span className="font-mono-lux max-w-[110px] truncate">
                            {r.transaction_id || (r.proof_path ? "Screenshot" : "—")}
                          </span>
                        </button>
                      ) : (
                        <span className="text-zinc-600 text-xs" data-testid={`proof-none-${r.id}`}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        {r.payment_status !== "paid" && r.total_amount > 0 && (
                          <button
                            onClick={() => remind(r)}
                            disabled={remindingId === r.id}
                            className="p-2 rounded-lg text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 relative"
                            data-testid={`remind-button-${r.id}`}
                            title={r.reminder_sent_at ? `Reminded ${r.reminder_count}x · last ${r.reminder_sent_at.slice(0, 10)}` : "Send payment reminder now"}
                          >
                            {remindingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
                            {r.reminder_count > 0 && (
                              <span className="absolute -top-0.5 -right-0.5 text-[9px] font-mono-lux bg-amber-400 text-black rounded-full w-3.5 h-3.5 flex items-center justify-center" data-testid={`remind-count-${r.id}`}>
                                {r.reminder_count}
                              </span>
                            )}
                          </button>
                        )}
                      <button
                        onClick={() => remove(r.id)}
                        className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-button-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-4">
          Showing {filtered.length} of {rows.length} registrations · Edit "Paid" amount and press Tab/click away to save.
          {" "}· Payment reminders auto-send once via email{stats?.whatsapp_configured ? " + WhatsApp" : ""} after 48h pending
          {!stats?.whatsapp_configured && (
            <span className="inline-flex items-center gap-1 ml-1 text-amber-400/80" data-testid="whatsapp-not-configured-note">
              <MessageCircle className="w-3 h-3" /> WhatsApp auto-send off (add Twilio keys); the bell opens a WhatsApp chat link instead
            </span>
          )}
        </p>
      </div>
      <ProofViewer row={proofRow} onClose={() => setProofRow(null)} />
    </div>
  );
}
